/**
 * Fila Configuration Storage Utilities
 *
 * Funções para salvar e carregar configurações do localStorage com sessionStorage cache
 */

import type { FilaConfig, FilaConfigValidation } from '../types/config';
import { DEFAULT_FILA_CONFIG, FILA_CONFIG_STORAGE_KEY } from '../types/config';

// Chaves para sessionStorage
const SESSION_CONFIG_KEY = 'fila_config_session';
const SESSION_TIMESTAMP_KEY = 'fila_config_timestamp';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Salva configurações no sessionStorage para cache rápido
 */
function saveSessionCache(config: FilaConfig): void {
  try {
    console.log('💾 Salvando no sessionStorage cache:', SESSION_CONFIG_KEY);
    const configJson = JSON.stringify(config);
    console.log('📋 Tamanho do config:', configJson.length, 'bytes');

    sessionStorage.setItem(SESSION_CONFIG_KEY, configJson);
    sessionStorage.setItem(SESSION_TIMESTAMP_KEY, Date.now().toString());

    // Verificar se salvou corretamente
    const saved = sessionStorage.getItem(SESSION_CONFIG_KEY);
    if (saved) {
      console.log('✅ SessionStorage cache salvo com sucesso');
    } else {
      console.error('❌ Falha ao salvar no sessionStorage cache');
    }
  } catch (error) {
    console.error('❌ Erro ao salvar cache sessionStorage:', error);
    console.error('Stack trace:', error instanceof Error ? error.stack : 'No stack available');
  }
}

/**
 * Carrega configurações do sessionStorage se ainda for válido
 */
function loadSessionCache(): FilaConfig | null {
  try {
    console.log('🔍 Verificando sessionStorage cache...');
    const cached = sessionStorage.getItem(SESSION_CONFIG_KEY);
    const timestamp = sessionStorage.getItem(SESSION_TIMESTAMP_KEY);

    console.log('📋 Cache encontrado:', cached ? 'SIM' : 'NÃO');
    console.log('📋 Timestamp encontrado:', timestamp ? 'SIM' : 'NÃO');

    if (!cached || !timestamp) {
      console.log('⚠️ Cache ou timestamp não encontrados');
      return null;
    }

    const now = Date.now();
    const cacheAge = now - parseInt(timestamp);
    console.log('⏰ Idade do cache:', Math.round(cacheAge / 1000), 'segundos');
    console.log('⏰ Duração máxima:', Math.round(CACHE_DURATION / 1000), 'segundos');

    if (cacheAge > CACHE_DURATION) {
      console.log('⚠️ Cache expirado, limpando...');
      // Cache expirado, limpar
      sessionStorage.removeItem(SESSION_CONFIG_KEY);
      sessionStorage.removeItem(SESSION_TIMESTAMP_KEY);
      return null;
    }

    console.log('✅ Parseando cache do sessionStorage...');
    const config = JSON.parse(cached) as FilaConfig;
    console.log('📋 Configuração do cache:', config);
    return config;
  } catch (error) {
    console.error('❌ Erro ao carregar cache sessionStorage:', error);
    console.error('Stack trace:', error instanceof Error ? error.stack : 'No stack available');
    return null;
  }
}

/**
 * Limpa cache do sessionStorage
 */
function clearSessionCache(): void {
  try {
    sessionStorage.removeItem(SESSION_CONFIG_KEY);
    sessionStorage.removeItem(SESSION_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Erro ao limpar cache sessionStorage:', error);
  }
}

/**
 * Valida configurações da fila (modo tolerante - apenas warnings críticos)
 */
export function validateFilaConfig(config: Partial<FilaConfig>): FilaConfigValidation {
  const errors: Record<string, string> = {};

  // Validar tipo de pedido - menos restritivo
  if (config.orderType && !['lunchbox', 'queue'].includes(config.orderType)) {
    console.warn('⚠️ orderType inválido detectado, será corrigido para default');
    errors.orderType = 'Tipo de pedido inválido - será corrigido';
  }

  // Validar colunas visíveis - garantir que pelo menos uma esteja visível
  if (config.visibleColumns) {
    const allColumns = Object.values(config.visibleColumns);
    if (allColumns.every(column => !column)) {
      console.warn('⚠️ Nenhuma coluna visível, será corrigido para defaults');
      errors.visibleColumns = 'Pelo menos uma coluna deve estar visível - será corrigido';
    }
  }

  // Validar modo de atualização do WebSocket - menos restritivo
  if (config.websocketUpdateMode && !['replace', 'update-status'].includes(config.websocketUpdateMode)) {
    console.warn('⚠️ websocketUpdateMode inválido, será corrigido para default');
    errors.websocketUpdateMode = 'Modo inválido - será corrigido';
  }

  // selectedInterval não é mais um erro crítico, apenas um warning
  if (config.autoIntervalChange === false && !config.selectedInterval && !config.currentInterval) {
    console.warn('⚠️ Modo manual sem intervalo selecionado, sistema usará gerenciamento automático');
    // Não é mais um erro que bloqueia o funcionamento
  }

  return {
    isValid: true, // Sempre válido agora, os erros são corrigidos automaticamente
    errors,
  };
}

/**
 * Salva configurações no localStorage com cache no sessionStorage
 */
export function saveFilaConfig(config: FilaConfig): boolean {
  try {
    const validation = validateFilaConfig(config);

    // Log para debug da validação
    console.log('🔍 Validação da configuração:', {
      config,
      validation,
      errorsCount: Object.keys(validation.errors).length
    });

    // Como isValid sempre retorna true, verificar se há erros críticos
    if (Object.keys(validation.errors).length > 0) {
      console.warn('⚠️ Alertas de validação encontrados:', validation.errors);
      // Não bloquear salvamento, apenas registrar os warnings
    }

    // Salvar no localStorage (persistente)
    console.log('💾 Salvando no localStorage:', FILA_CONFIG_STORAGE_KEY);
    localStorage.setItem(FILA_CONFIG_STORAGE_KEY, JSON.stringify(config));

    // Salvar no sessionStorage (cache rápido)
    console.log('💾 Salvando no sessionStorage cache');
    saveSessionCache(config);

    // Verificar se salvou corretamente
    const savedConfig = localStorage.getItem(FILA_CONFIG_STORAGE_KEY);
    if (!savedConfig) {
      console.error('❌ Falha ao salvar no localStorage');
      return false;
    }

    console.log('✅ Configuração salva com sucesso no storage');

    // Disparar evento customizado para notificar mudanças na mesma aba
    window.dispatchEvent(new CustomEvent('filaConfigChanged', {
      detail: { config, timestamp: Date.now() }
    }));

    return true;
  } catch (error) {
    console.error('❌ Erro ao salvar configurações da fila:', error);
    console.error('Stack trace:', error instanceof Error ? error.stack : 'No stack available');
    return false;
  }
}

/**
 * Carrega configurações do localStorage com cache do sessionStorage e recuperação automática
 */
export function loadFilaConfig(): FilaConfig {
  console.log('🔍 Carregando configurações da fila...');

  try {
    // 1. Tentar carregar do sessionStorage primeiro (cache rápido)
    console.log('📝 Verificando cache sessionStorage...');
    const sessionConfig = loadSessionCache();
    if (sessionConfig) {
      console.log('✅ Carregando configurações do cache sessionStorage:', sessionConfig);
      return sessionConfig;
    }
    console.log('⚠️ Nenhuma configuração válida no sessionStorage cache');

    // 2. Tentar carregar do localStorage (persistente)
    console.log('📝 Verificando localStorage...');
    const stored = localStorage.getItem(FILA_CONFIG_STORAGE_KEY);
    console.log('📋 Dados encontrados no localStorage:', stored ? 'SIM' : 'NÃO');

    if (!stored) {
      console.log('📝 Nenhuma configuração encontrada, usando defaults com recuperação de establishment');
      const defaultConfig = getConfigWithEstablishment(DEFAULT_FILA_CONFIG);
      console.log('🔧 Configuração default com establishment:', defaultConfig);
      saveSessionCache(defaultConfig); // Salvar no cache para próximas leituras
      return defaultConfig;
    }

    console.log('📝 Parseando configurações do localStorage...');
    const config = JSON.parse(stored) as FilaConfig;
    console.log('📋 Configuração parseada:', config);

    console.log('🔍 Validando configuração...');
    const validation = validateFilaConfig(config);
    console.log('📋 Resultado da validação:', validation);

    if (!validation.isValid) {
      console.warn('⚠️ Configurações inválidas no storage, tentando recuperação:', validation.errors);
      const recoveredConfig = recoverConfig(config, validation.errors);
      console.log('🔧 Configuração recuperada:', recoveredConfig);
      saveSessionCache(recoveredConfig); // Salvar no cache
      return recoveredConfig;
    }

    // Garantir que establishmentId sempre exista
    console.log('🔝 Garantindo establishmentId...');
    const finalConfig = getConfigWithEstablishment(config);
    console.log('✅ Configuração final:', finalConfig);
    saveSessionCache(finalConfig); // Salvar no cache para próximas leituras
    return finalConfig;
  } catch (error) {
    console.error('❌ Erro ao carregar configurações da fila:', error);
    console.error('Stack trace:', error instanceof Error ? error.stack : 'No stack available');
    const fallbackConfig = getConfigWithEstablishment(DEFAULT_FILA_CONFIG);
    console.log('🔧 Usando configuração fallback:', fallbackConfig);
    saveSessionCache(fallbackConfig); // Salvar fallback no cache
    return fallbackConfig;
  }
}

/**
 * Garante que establishmentId seja preenchido com valores disponíveis
 */
function getConfigWithEstablishment(config: FilaConfig): FilaConfig {
  if (config.establishmentId) {
    return config;
  }

  // Tentar obter establishmentId de diferentes fontes
  const establishmentId = getEstablishmentIdFromSources();

  if (establishmentId) {
    console.log('🏪 EstablishmentId recuperado automaticamente:', establishmentId);
    return { ...config, establishmentId };
  }

  console.warn('⚠️ Nenhum establishmentId encontrado, sistema funcionará em modo limitado');
  return config;
}

/**
 * Obtém establishmentId de diferentes fontes em ordem de prioridade
 */
function getEstablishmentIdFromSources(): string | null {
  try {
    // 1. Tentar do localStorage (sessão atual)
    const currentEstablishment = localStorage.getItem('current_establishment');
    if (currentEstablishment) {
      const parsed = JSON.parse(currentEstablishment);
      if (parsed?.id) {
        return parsed.id;
      }
    }

    // 2. Tentar do user profile
    const userInfo = localStorage.getItem('user_info');
    if (userInfo) {
      const parsed = JSON.parse(userInfo);
      const activeProfile = parsed?.profiles?.find((profile: any) => profile.active);
      if (activeProfile?.establishment_id) {
        return activeProfile.establishment_id;
      }
    }

    return null;
  } catch (error) {
    console.error('Erro ao buscar establishmentId:', error);
    return null;
  }
}

/**
 * Tenta recuperar configurações inválidas removendo apenas os campos problemáticos
 */
function recoverConfig(config: Partial<FilaConfig>, errors: Record<string, string>): FilaConfig {
  const recoveredConfig = { ...DEFAULT_FILA_CONFIG, ...config };

  // Campos inválidos voltam ao padrão em vez de serem apagados: os dois são
  // obrigatórios em FilaConfig, e `delete` deixaria a configuração incompleta
  // — recoveredConfig já parte de DEFAULT_FILA_CONFIG.
  if (errors.orderType) {
    console.log('🔄 Restaurando orderType padrão');
    recoveredConfig.orderType = DEFAULT_FILA_CONFIG.orderType;
  }

  if (errors.websocketUpdateMode) {
    console.log('🔄 Restaurando websocketUpdateMode padrão');
    recoveredConfig.websocketUpdateMode = DEFAULT_FILA_CONFIG.websocketUpdateMode;
  }

  if (errors.visibleColumns) {
    console.log('🔄 Resetando visibleColumns para defaults');
    recoveredConfig.visibleColumns = DEFAULT_FILA_CONFIG.visibleColumns;
  }

  // Não limpar selectedInterval automaticamente - permitir funcionamento sem ele
  if (errors.selectedInterval) {
    console.log('⚠️ selectedInterval inválido, mas mantendo para允许 funcionamento');
  }

  console.log('✅ Configuração recuperada:', recoveredConfig);
  return getConfigWithEstablishment(recoveredConfig as FilaConfig);
}

/**
 * Remove configurações do localStorage e sessionStorage
 */
export function clearFilaConfig(): boolean {
  try {
    localStorage.removeItem(FILA_CONFIG_STORAGE_KEY);
    clearSessionCache(); // Limpar também o cache sessionStorage
    return true;
  } catch (error) {
    console.error('Erro ao limpar configurações da fila:', error);
    return false;
  }
}

/**
 * Verifica se há configurações salvas
 */
export function hasStoredFilaConfig(): boolean {
  try {
    const stored = localStorage.getItem(FILA_CONFIG_STORAGE_KEY);
    return stored !== null;
  } catch (error) {
    console.error('Erro ao verificar configurações salvas:', error);
    return false;
  }
}